import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/context';
import createClientRequest from '@/proxy';
import { retrieveSubscription, getSimPluginType } from '@/api/requestPath';
import useCountryGate from '@/hooks/useCountryGate';
import NetworkErrorPage from '@/components/NetworkErrorPage';

const hasActivePlan = (sub) => Boolean(sub && sub.status !== 6 && sub.planType !== 'pt_vip_0');

/**
 * 订阅流程守卫（会话首跳执行一次，决定落点）：
 * 有生效套餐 → 只按订阅渠道定区域，放行当前页；
 * 没套餐 → 查国家，已确认去套餐页，未确认去选择国家页（老 App 无桥则按非俄进套餐页）。
 * 详情与 SIM 插件能力一并在此拉取，页面从 context 读，不再各调一次。
 */
const RegionGate = ({ children }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const clientRequest = useMemo(() => createClientRequest(t, i18n), [t, i18n]);
  const { regionChecked, setRegionChecked, setDetails, setSimPluginType, setLoading } = useSubscription();
  const { applyChannel, resolvePlansEntry } = useCountryGate();
  // 守卫只跑一次的标记（防并发重复请求）
  const gateStartedRef = useRef(false);
  const [gateError, setGateError] = useState(false);

  const runGate = useCallback(async () => {
    if (regionChecked || gateStartedRef.current) return;
    gateStartedRef.current = true;
    try {
      const [subRes, simRes] = await Promise.all([
        clientRequest({ requestPath: retrieveSubscription, requestData: { applicationCode: '70mai' } }),
        clientRequest({ requestPath: getSimPluginType, requestData: {} }),
      ]);
      const obj = subRes?.resultBodyObject;
      const simPluginType = simRes?.resultBodyObject;
      if (obj) setDetails(obj);
      if (simPluginType) setSimPluginType(simPluginType);

      setGateError(false);
      const sub = obj?.userSubscription;
      if (hasActivePlan(sub)) {
        applyChannel(sub, '');
        setRegionChecked(true);
        setLoading(false);
      } else {
        const dest = await resolvePlansEntry(sub);
        setRegionChecked(true);
        setLoading(false);
        navigate(dest, { replace: true });
      }
    } catch (e) {
      console.warn('[RegionGate] gate failed', e);
      setGateError(true);
      setLoading(false);
    }
  }, [regionChecked, clientRequest, applyChannel, resolvePlansEntry, navigate, setDetails, setSimPluginType, setRegionChecked, setLoading]);

  const handleRetry = async () => {
    gateStartedRef.current = false;
    await runGate();
  };

  useEffect(() => {
    runGate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (gateError) return <NetworkErrorPage onRetry={handleRetry} />;

  return <>{children}</>;
};

export default RegionGate;
